import { createStore } from "treble-gsm";
import { calcGridHeight, processState } from "../middleware";

export interface IUIStore {
  gridRef: any;
  gridTableRef: any;
  gridTBodyRef: any;
  gridHeight: string;
  fallBackGridHeight: string;
  loadingState: boolean;
  columnWidths: {
    target: string;
    width: string | "auto";
  }[];
  gridPagingOptions: {
    page: boolean;
    size: number;
    groupSize: number;
  };
  activeGridPage: number;
  renderedRowGroups: [][];
  CustomRow: null | any;
  customRowProps: { [key: string]: any };
  columnFields: {
    field: string;
    title: string;
    enabled: boolean;
    trebleKey: number;
  }[];
  ExpandableRow: any;
  expandableCheck: (item: any) => boolean;
  exports: {
    pdf: boolean;
    excel: boolean;
    options: boolean;
    customOptions: {
      type: 'excel' | 'pdf' | string,
      label: string,
      onClick: () => void
    }[] | boolean
  };
  GroupMeta: null | any;
  scrollTrigger: () => void | boolean;
  placeholderRowState: boolean;
  columnTitleState: boolean;
  scrollLoadingState: boolean,
  loadMoreDataRef: any,
  preventGridScrollEvents: boolean,
  renderMoreDataRef: any,
  footerColumnSelector: boolean,
  stickyColumns: boolean,
  theadRef: any
}

const actionKeys = {
  ['updateGridRef']: 'updateGridRef',
  ['updateGridTableRef']: 'updateGridTableRef',
  ['updateGridTBodyRef']: 'updateGridTBodyRef',
  ['updateGridHeight']: 'updateGridHeight',
  ['calculateFallBackGridHeight']: 'calculateFallBackGridHeight',
  ['updateLoadingState']: 'updateLoadingState',
  ['setColumnWidths']: 'setColumnWidths',
  ['updateGridPagingOptions']: 'updateGridPagingOptions',
  ['updateActiveGridPage']: 'updateActiveGridPage',
  ['updateRenderedRowGroups']: 'updateRenderedRowGroups',
  ['createCustomRow']: 'createCustomRow',
  ['updateCustomRowProps']: 'updateCustomRowProps',
  ['createColumnFields']: 'createColumnFields',
  ['createExpandableRow']: 'createExpandableRow',
  ['setExports']: 'setExports',
  ['createGroupMeta']: 'createGroupMeta',
  ['setScrollTriggerFunction']: 'setScrollTriggerFunction',
  ['setPlaceholderRowState']: 'setPlaceholderRowState',
  ['setColumnTitleState']: 'setColumnTitleState',
  ['setColumnState']: 'setColumnState',
  ['setExpandableCheck']: 'setExpandableCheck',
  ['setDebounceFilterTime']: 'setDebounceFilterTime',
  ['setScrollLoadingState']: 'setScrollLoadingState',
  ['updateLoadMoreDataRef']: 'updateLoadMoreDataRef',
  ['setPreventGridScrollEvents']: 'setPreventGridScrollEvents',
  ['setRenderMoreDataRef']: 'setRenderMoreDataRef',
  ['updateTheadRef']: 'updateTheadRef',
  ['showFooterColumnSelector']: 'showFooterColumnSelector',
  ['setStickyColumnState']: 'setStickyColumnState',
  ['setOnSelect']: 'setOnSelect'
}

export type TUIActions = typeof actionKeys;

const UIStore = createStore([
  {
    action: actionKeys.updateGridRef,
    state: {
      gridRef: null,
    },
  },
  {
    action: actionKeys.updateGridTableRef,
    state: {
      gridTableRef: null,
    },
  },
  {
    action: actionKeys.updateGridTBodyRef,
    state: {
      gridTBodyRef: null
    }
  },
  {
    action: actionKeys.updateGridHeight,
    state: {
      gridHeight: "400px",
    },
  },
  {
    action: actionKeys.calculateFallBackGridHeight,
    state: {
      fallBackGridHeight: "",
    },
    features: {
      process: (data) => calcGridHeight(data),
    },
  },
  {
    action: actionKeys.updateLoadingState,
    state: {
      loadingState: true,
    },
  },
  {
    action: actionKeys.setColumnWidths,
    state: {
      columnWidths: [],
    },
  },
  {
    action: actionKeys.updateGridPagingOptions,
    state: {
      gridPagingOptions: {
        page: false,
        size: 100,
        groupSize: 15,
      },
    },
  },
  {
    action: actionKeys.updateActiveGridPage,
    state: {
      activeGridPage: 0,
    },
  },
  {
    action: actionKeys.updateRenderedRowGroups,
    state: {
      renderedRowGroups: [],
    },
    features: {
      process: (value) => processState(value.dispatchValue),
    },
  },
  {
    action: actionKeys.createCustomRow,
    state: {
      CustomRow: null,
    },
  },
  {
    action: actionKeys.updateCustomRowProps,
    state: {
      customRowProps: {},
    },
  },
  {
    action: actionKeys.createColumnFields,
    state: {
      columnFields: [],
    },
    features: {
      keys: true,
    },
  },
  {
    action: actionKeys.createExpandableRow,
    state: {
      ExpandableRow: false,
    },
  },
  {
    action: actionKeys.setExports,
    state: {
      exports: {
        pdf: false,
        excel: false,
        options: false,
        customOptions: false
      },
    },
  },
  {
    action: actionKeys.createGroupMeta,
    state: {
      GroupMeta: null,
    },
  },
  {
    action: actionKeys.setScrollTriggerFunction,
    state: {
      scrollTrigger: false
    }
  },
  {
    action: actionKeys.setPlaceholderRowState,
    state: {
      placeholderRowState: false
    }
  }, {
    action: actionKeys.setColumnTitleState,
    state: {
      columnTitleState: true
    }
  },
  {
    action: actionKeys.setColumnState,
    state: {
      columnState: []
    }
  },
  {
    action: actionKeys.setExpandableCheck,
    state: {
      expandableCheck: (value: any) => value
    }
  },
  {
    action: actionKeys.setDebounceFilterTime,
    state: {
      debounceFilterTime: 400
    }
  },
  {
    action: actionKeys.setScrollLoadingState,
    state: {
      scrollLoadingState: false
    }
  },
  {
    action: actionKeys.updateLoadMoreDataRef,
    state: {
      loadMoreDataRef: null
    }
  },
  {
    action: actionKeys.setPreventGridScrollEvents,
    state: {
      preventGridScrollEvents: false
    }
  },
  {
    action: actionKeys.setRenderMoreDataRef,
    state: {
      renderMoreDataRef: null
    }
  },
  {
    action: actionKeys.updateTheadRef,
    state: {
      theadRef: null
    }
  },
  {
    action: actionKeys.showFooterColumnSelector,
    state: {
      footerColumnSelector: false
    }
  },
  {
    action: actionKeys.setStickyColumnState,
    state: {
      stickyColumns: true
    }
  },
  {
    action: actionKeys.setOnSelect,
    state: {
      onSelect: null
    }
  }
]);

export default UIStore;
